// Shared Statsig client singleton for non-React consumers. `@axo/feature-flags`
// OWNS the single client-side Statsig SDK instance; other packages (notably
// `@axo/observability`'s StatsigService) borrow it via these accessors instead
// of creating/holding their own. Moved verbatim (functions renamed only) from
// `@axo/observability`'s `experimentation/statsig/client/statsig.client.ts`
// per specs/feature-flags-native-exposure-phase2 (Phase A).
//
import type { StatsigUser } from '@statsig/js-client';
import {
  resolveUserUnitExperiments,
  unitIdOf,
  type StatsigClientLike,
} from './user-unit-resolver';
import { isFreshSource, isLoadedReason } from './value-reason';
import { safely } from '../../core/safely';
import { _flagStore, type StatsigUnitRequest } from '../../core/flag-store';
import { isInternalTraffic } from '../../core/internal-traffic';
import {
  experimentKeysForUnit,
  type UserUnit,
} from '../../registry/experiment-unit';

// Use the official StatsigClient type to avoid drift/mismatch without creating a runtime import
// eslint-disable-next-line @typescript-eslint/consistent-type-imports
type StatsigClient = import('@statsig/js-client').StatsigClient;

let currentClient: StatsigClient | null = null;
// Unsubscribes the user-unit resolver from the currently held client.
let detach: (() => void) | null = null;
let unavailable = false;
let started = false;
const clientListeners = new Set<() => void>();
const notifyClientListeners = () =>
  clientListeners.forEach((listener) => safely('client listener', listener));
const valuesListeners = new Set<() => void>();

// This holder wraps `@statsig/js-client`, a browser SDK, so it has no
// meaning on the server. `client-init.ts` is `'use client'`, but `'use
// client'` modules still execute server-side for the initial HTML — without
// this guard, each SSR render would publish a per-request client into
// `currentClient`, a module-scoped variable that on the server is
// process-global and shared across every concurrent request (these apps are
// multi-tenant: one process serves many brands), leaking one tenant's SDK
// key and user context into another tenant's render. Guarded once, here, so
// the invariant can't be forgotten at a call site: writes are dropped and
// reads are always null on the server.
const isServer = () => typeof window === 'undefined';

// MV-0-017 — every `values_updated` on the held client re-evaluates the
// user-unit experiments into the flag store (the SDK's PUBLIC `on`/`off`; `$on`
// has no `$off`), and once on publish. MVX-371 N12: each event's
// `values.source` is recorded per client (see `hasFreshValues`).
export function setStatsigClient(client: StatsigClient | null): void {
  if (isServer()) return;
  detach?.();
  detach = null;
  // A withdrawn client (its provider unmounted) leaves nothing on its way.
  if (!client && currentClient) started = false;
  currentClient = client ?? null;
  if (currentClient) unavailable = false;
  notifyClientListeners();
  if (!currentClient) return;
  const c = currentClient;
  const resolve = () => resolveOn(c);
  const onValues = (event?: { values?: { source?: string } | null }) => {
    lastSource.set(c, event?.values?.source);
    resolve();
    valuesListeners.forEach((listener) => safely('values listener', listener));
  };
  // A client without the event API (minimal test doubles) is still published.
  if (typeof c.on === 'function' && typeof c.off === 'function') {
    c.on('values_updated', onValues);
    detach = () => c.off('values_updated', onValues);
  }
  resolve();
  // Apply a request this client doesn't carry yet; settle a round a replaced
  // client left open (an update in flight on THIS client settles itself).
  for (const unit of UNITS) {
    const request = requestFor(unit);
    if (!request) continue;
    if (unitIdOf(clientUser(c), unit) !== request.id) {
      applyRequest(unit, c, request);
    } else if (inFlightOn.get(request) !== c) {
      settleRound(request);
    }
  }
}

/** The provider settled without a client: Statsig is off for this page, or
 *  its init failed or timed out. A client published later clears it. */
export const markStatsigUnavailable = (): void => {
  if (isServer() || unavailable || currentClient) return;
  unavailable = true;
  notifyClientListeners();
};

/** A Statsig entry path began on this page, so a client or "none is coming"
 *  will follow. Without it, nothing will ever publish either. */
export const markStatsigStarted = (): void => {
  if (isServer()) return;
  started = true;
};

/** Whether a client is published, settled as unavailable, or on its way. */
export const isStatsigStarted = (): boolean =>
  started || unavailable || !!currentClient;

/** Test-only: forget a settled "no client" and a started provider. */
export const _resetStatsigUnavailable = (): void => {
  unavailable = false;
  started = false;
};

/** No client, and none is coming. */
export const isStatsigUnavailable = (): boolean => unavailable;

/** Runs when a client is published or unpublished, and when the provider
 *  settles without one. */
export const subscribeStatsigClient = (listener: () => void): (() => void) => {
  clientListeners.add(listener);
  return () => clientListeners.delete(listener);
};

/** Runs when the held client's values update. */
export const subscribeStatsigValues = (listener: () => void): (() => void) => {
  valuesListeners.add(listener);
  return () => valuesListeners.delete(listener);
};

/** The client is done loading and holds values for its user: a failed or
 *  timed-out load ends `Ready` too, with none (or a `NoValues` /
 *  `NetworkError` source). */
export const hasLoadedValues = (c: StatsigClient): boolean => {
  if (c.loadingStatus !== 'Ready') return false;
  try {
    return (
      c.getContext?.()?.values != null &&
      isLoadedReason(lastSource.get(c) ?? '')
    );
  } catch {
    return false;
  }
};

export function getStatsigClient(): StatsigClient | null {
  if (isServer()) return null;
  return currentClient;
}

export const isStatsigClientReady = (): boolean => Boolean(getStatsigClient());

// ── MV-0-017 D5 / MVX-371 — a unit id on the Statsig user, bounded by the SDK ─

export type IdentifyCustomerOptions = {
  /** Passed to `updateUserAsync` as the SDK's own `timeoutMs` — the bound. */
  timeoutMs: number;
  /** Runs once if a user-unit experiment is still unresolved for the id
   *  when the bounded update settles (success, SDK timeout or error). */
  onTimeout?: () => void;
};

const UNITS: readonly UserUnit[] = ['userID', 'personID'];

/** Each client's last values source (N12); absent = freshness unknown. */
const lastSource = new WeakMap<StatsigClient, string | undefined>();

/** The client has values for its current user, fetched from the network. */
const hasFreshValues = (c: StatsigClient): boolean =>
  c.loadingStatus !== 'Loading' && isFreshSource(lastSource.get(c));

/** The held client's user carries `unitId` as `unit`'s id, with values
 *  fetched for it: what it answers is that unit's own. */
export const carriesWithFreshValues = (
  unit: UserUnit,
  unitId: string
): boolean => {
  const c = getStatsigClient();
  return !!c && unitIdOf(clientUser(c), unit) === unitId && hasFreshValues(c);
};

/** U12 — a request's awaitable round: settles with its update, never rejects. */
type Round = { promise: Promise<void>; settle: () => void };
const rounds = new WeakMap<StatsigUnitRequest, Round>();

const openRound = (request: StatsigUnitRequest): Round => {
  const open = rounds.get(request);
  if (open) return open;
  let settle = () => {};
  const promise = new Promise<void>((resolve) => {
    settle = () => {
      rounds.delete(request);
      resolve();
    };
  });
  const round = { promise, settle };
  rounds.set(request, round);
  return round;
};

const settleRound = (request: StatsigUnitRequest): void =>
  rounds.get(request)?.settle();

/** Re-evaluate the user-unit experiments on `c`'s values; never throws. */
const resolveOn = (c: StatsigClient): void => {
  try {
    resolveUserUnitExperiments(c as unknown as StatsigClientLike);
  } catch (error) {
    console.error(
      '[feature-flags:statsig] user-unit experiment resolution failed',
      error
    );
  }
};

const roundPromise = (request: StatsigUnitRequest): Promise<void> =>
  rounds.get(request)?.promise ?? Promise.resolve();

/** The client a request's bounded update is in flight on. */
const inFlightOn = new WeakMap<StatsigUnitRequest, StatsigClient>();
/** Each client's latest unit update (it carries both units' ids). */
const latestUnitUpdate = new WeakMap<StatsigClient, Promise<unknown>>();

const requestFor = (unit: UserUnit): StatsigUnitRequest | null => {
  const s = _flagStore.getState();
  return unit === 'userID' ? s.statsigCustomer : s.statsigPerson;
};

const recordRequest = (unit: UserUnit, request: StatsigUnitRequest): void => {
  const s = _flagStore.getState();
  if (unit === 'userID') s.requestStatsigCustomer(request);
  else s.requestStatsigPerson(request);
};

// A request leaving the store (superseded, or reset) settles its round.
_flagStore.subscribe(
  (s) => s.statsigCustomer,
  (_next, prev) => prev && settleRound(prev)
);
_flagStore.subscribe(
  (s) => s.statsigPerson,
  (_next, prev) => prev && settleRound(prev)
);

/** The client's user (`{}` if unreadable), with the SDK's root `stableID`
 *  filled in when the user lacks one. */
const clientUser = (c: StatsigClient): StatsigUser => {
  try {
    const ctx = c.getContext?.();
    const user = ctx?.user ?? {};
    const stableID = user.customIDs?.stableID ?? ctx?.stableID;
    return stableID && !user.customIDs?.stableID
      ? { ...user, customIDs: { ...user.customIDs, stableID } }
      : user;
  } catch {
    return {};
  }
};

/** Dimensions added to the user's `custom`; `undefined` values skipped. */
export type CustomDimensions = Record<string, string | undefined>;

const withCustom = (
  user: StatsigUser,
  custom: CustomDimensions | undefined
): StatsigUser => {
  const added = Object.fromEntries(
    Object.entries(custom ?? {}).filter(([, v]) => v !== undefined)
  ) as Record<string, string>;
  const changes = Object.entries(added).some(
    ([k, v]) => user.custom?.[k] !== v
  );
  return changes ? { ...user, custom: { ...user.custom, ...added } } : user;
};

/** The user with `unitId` as `unit`'s id, keeping stable ID, person and
 *  `custom` (N7); `email` only for the same customer. */
const userWith = (
  c: StatsigClient,
  unit: UserUnit,
  unitId: string
): StatsigUser => {
  const { userID, email, ...rest } = clientUser(c);
  if (unit === 'personID') {
    return {
      ...rest,
      ...(userID ? { userID } : {}),
      ...(email ? { email } : {}),
      customIDs: { ...rest.customIDs, personID: unitId },
    };
  }
  return {
    ...rest,
    ...(userID === unitId && email ? { email } : {}),
    userID: unitId,
  };
};

/** The request's update settled: time out its unit's unresolved keys
 *  (sticky), `onTimeout` once. On a replaced client only the round settles. */
const settleRequest = (
  unit: UserUnit,
  c: StatsigClient,
  request: StatsigUnitRequest
) => {
  if (inFlightOn.get(request) === c) inFlightOn.delete(request);
  if (currentClient !== c) {
    if (!inFlightOn.has(request)) settleRound(request);
    return;
  }
  const store = _flagStore.getState();
  if (requestFor(unit) !== request) return;
  let timedOut = false;
  try {
    for (const key of experimentKeysForUnit(unit)) {
      if (store.timeOutUserResolution(key, request.id)) timedOut = true;
    }
  } finally {
    // A store listener that throws must never leave the round open (U12).
    settleRound(request);
  }
  if (timedOut) request.onTimeout?.();
};

/** The SDK's result for an update a newer one replaced mid-flight. */
const SUPERSEDED_UPDATE = 'User changed during update';

const wasSuperseded = (details: unknown): boolean =>
  (details as { error?: { message?: string } } | undefined)?.error?.message ===
  SUPERSEDED_UPDATE;

/** Settle `request` after the client's latest unit update (which carries
 *  its id too), following newer ones. */
const settleAfterLatest = (
  unit: UserUnit,
  c: StatsigClient,
  request: StatsigUnitRequest,
  latest: Promise<unknown>
): void => {
  latest
    .catch(() => undefined)
    .then(() => {
      const newer = latestUnitUpdate.get(c);
      if (
        newer &&
        newer !== latest &&
        currentClient === c &&
        requestFor(unit) === request
      ) {
        settleAfterLatest(unit, c, request, newer);
        return;
      }
      settleRequest(unit, c, request);
    });
};

/** `updateUserAsync` bounded by `timeoutMs`. Superseded by our own update for
 *  the other unit → wait for it; by another writer for the same id still in
 *  flight → re-issue within the remaining budget; else settle. */
const applyRequest = (
  unit: UserUnit,
  c: StatsigClient,
  request: StatsigUnitRequest,
  deadline = Date.now() + request.timeoutMs
): void => {
  openRound(request);
  const budget = deadline - Date.now();
  if (budget <= 0 || typeof c.updateUserAsync !== 'function') {
    settleRequest(unit, c, request);
    return;
  }
  let update: Promise<unknown>;
  try {
    update = c.updateUserAsync(userWith(c, unit, request.id), {
      timeoutMs: budget,
    });
  } catch (error) {
    update = Promise.reject(error);
  }
  inFlightOn.set(request, c);
  latestUnitUpdate.set(c, update);
  update
    .catch(() => undefined)
    .then((details) => {
      const supersededCarryingId =
        wasSuperseded(details) &&
        currentClient === c &&
        requestFor(unit) === request &&
        unitIdOf(clientUser(c), unit) === request.id;
      if (supersededCarryingId) {
        const latest = latestUnitUpdate.get(c);
        if (latest && latest !== update) {
          settleAfterLatest(unit, c, request, latest);
          return;
        }
        if (c.loadingStatus === 'Loading' && deadline - Date.now() > 0) {
          applyRequest(unit, c, request, deadline);
          return;
        }
      }
      settleRequest(unit, c, request);
    });
};

/** Request `unitId` on the Statsig user (D5, U6, U12); never rejects. */
const identifyUnit = (
  unit: UserUnit,
  unitId: string,
  { timeoutMs, onTimeout }: IdentifyCustomerOptions
): Promise<void> => {
  if (isServer() || !unitId) return Promise.resolve();
  const existing = requestFor(unit);
  if (existing?.id === unitId) {
    // Re-apply only if the client doesn't carry it; no store write (no loop).
    const c = currentClient;
    if (c && unitIdOf(clientUser(c), unit) !== unitId)
      applyRequest(unit, c, existing);
    return roundPromise(existing);
  }
  const request: StatsigUnitRequest = { id: unitId, timeoutMs, onTimeout };
  const c = currentClient;
  // U12: carried with network values → no update. Else our own bounded update
  // (a re-entrant listener may apply it first; then it isn't applied twice).
  const carriedBefore = !!c && unitIdOf(clientUser(c), unit) === unitId;
  const fresh = carriedBefore && hasFreshValues(c);
  if (c && !fresh) openRound(request);
  recordRequest(unit, request);
  if (!c) return Promise.resolve();
  // Superseded by a re-entrant listener's other id: settle, never apply (U12).
  if (requestFor(unit) !== request) {
    settleRound(request);
    return Promise.resolve();
  }
  if (fresh) {
    // Resolve on the values held first: after a reset no `values_updated`
    // follows, and settling at once would time those keys out (U15).
    resolveOn(c);
    settleRequest(unit, c, request);
  } else if (carriedBefore || unitIdOf(clientUser(c), unit) !== unitId) {
    applyRequest(unit, c, request);
  }
  return roundPromise(request);
};

/** MV-0-017 / MVX-371 U12 — COMMAND: put `customerId` on the Statsig user,
 *  bounded by the SDK's `timeoutMs`; unresolved keys then time out (sticky),
 *  `onTimeout` once. The promise settles with the update and never rejects.
 *  Browser only; idempotent per customerId; a new one supersedes. */
export const identifyCustomer = (
  customerId: string,
  opts: IdentifyCustomerOptions
): Promise<void> => identifyUnit('userID', customerId, opts);

/** MVX-371 U6 — COMMAND: put `personId` on the Statsig client user as
 *  `customIDs.personID`, with `identifyCustomer`'s semantics for the
 *  `personID` unit. Never changes `userID`. */
export const identifyPerson = (
  personId: string,
  opts: IdentifyCustomerOptions
): Promise<void> => identifyUnit('personID', personId, opts);

export type IdentifyUserInput = {
  customerId: string;
  email?: string;
  /** Added to the user's `custom` dimensions (kept otherwise). */
  custom?: CustomDimensions;
};

/** U4/U17 — COMMAND: the session's own customer (observability's identify).
 *  A different requested customer wins: nothing sent. Never rejects. */
export async function identifyUser({
  customerId,
  email,
  custom,
}: IdentifyUserInput): Promise<void> {
  const c = getStatsigClient();
  if (!c || !customerId) return;
  const requested = _flagStore.getState().statsigCustomer?.id;
  if (requested && requested !== customerId) return;
  const { email: _email, ...rest } = userWith(c, 'userID', customerId);
  try {
    await c.updateUserAsync(
      withCustom({ ...rest, ...(email ? { email } : {}) }, custom)
    );
  } catch {
    // Same contract as the analytics sinks: identify never throws.
  }
}

/** U15/U17 — COMMAND: logout. `resetUserResolution`, then drop customer,
 *  email and person from the user (none to drop → no update). Never rejects. */
export async function resetIdentity(custom?: CustomDimensions): Promise<void> {
  if (isServer()) return;
  _flagStore.getState().resetUserResolution();
  const c = currentClient;
  if (!c) return;
  const { userID, email, customIDs, ...rest } = clientUser(c);
  const { personID, ...ids } = customIDs ?? {};
  const base = {
    ...rest,
    ...(Object.keys(ids).length ? { customIDs: ids } : {}),
  };
  const next = withCustom(base, custom);
  // Already anonymous (the other logout path ran first): nothing to send.
  if (!userID && !email && !personID && next === base) return;
  try {
    await c.updateUserAsync(next);
  } catch {
    // Same contract as the analytics sinks: reset never throws.
  }
}

/** The unit `key` is bucketed on, lowercased (`userid`, `stableid`, …), read
 *  with no exposure; `null` with no client or no value for it. */
export const gateUnit = (key: string): string | null => {
  try {
    return (
      getStatsigClient()
        ?.getFeatureGate(key, { disableExposureLog: true })
        ?.idType?.toLowerCase() ?? null
    );
  } catch {
    return null;
  }
};

/** MVX-491 / U14 — read a gate (logs its exposure); `false` with no client,
 *  no value or an error, and `false` with no exposure while the user lacks the
 *  gate's unit (read with exposure logging off, N14). Internal traffic reads
 *  the same value with no exposure. */
export function checkGate(key: string): boolean {
  const c = getStatsigClient();
  if (!c) return false;
  try {
    const idType = c.getFeatureGate(key, { disableExposureLog: true })?.idType;
    if (idType && idType.toLowerCase() !== 'stableid') {
      const user = clientUser(c);
      const unitId =
        idType.toLowerCase() === 'userid'
          ? user.userID
          : user.customIDs?.[idType] ?? user.customIDs?.[idType.toLowerCase()];
      if (!unitId) return false;
    }
    const value = isInternalTraffic()
      ? c.checkGate(key, { disableExposureLog: true })
      : c.checkGate(key);
    return value ?? false;
  } catch {
    return false;
  }
}
